import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { roleService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { canDelete, isSuperAdmin } from '../../utils/rolePermissions';

const RoleMaster = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Load roles from API
  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await roleService.getAll({ search: searchTerm });
      setRoles(response.data || []);
    } catch (error) {
      console.error('Error loading roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  // Reload roles when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadRoles();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (role = null) => {
    setEditingRole(role);
    setShowModal(true);
    if (role) {
      reset({
        name: role.name,
        slug: role.slug,
        is_active: role.is_active
      });
    } else {
      reset({ name: '', slug: '', is_active: true });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRole(null);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (editingRole) {
        // Update role
        await roleService.update(editingRole.id, data);
        toast.success('Role updated successfully!');
      } else {
        // Create new role
        await roleService.create(data);
        toast.success('Role created successfully!');
      }
      closeModal();
      loadRoles(); // Reload the list
    } catch (error) {
      console.error('Error saving role:', error);
      toast.error('Failed to save role');
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (id) => {
    // Check if Super Admin trying to delete
    if (isSuperAdmin(user)) {
      toast.error('Super Admin cannot delete records');
      return;
    }

    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        setLoading(true);
        await roleService.delete(id);
        toast.success('Role deleted successfully!');
        loadRoles(); // Reload the list
      } catch (error) {
        console.error('Error deleting role:', error);
        toast.error('Failed to delete role');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (role) => {
    try {
      setLoading(true);
      await roleService.update(role.id, { 
        name: role.name,
        slug: role.slug,
        is_active: !role.is_active 
      });
      toast.success('Role status updated!');
      loadRoles(); // Reload the list
    } catch (error) {
      console.error('Error updating role status:', error);
      toast.error('Failed to update role status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Role Master
          </h1>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Role
          </button>
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px', position: 'relative', maxWidth: '300px' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />
          <input
            type="text"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-box"
            style={{ paddingLeft: '40px' }}
          />
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Role Name</th>
                <th>Status</th>
                <th>Created Date</th>
                <th>Updated Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.id}</td>
                  <td style={{ fontWeight: '500' }}>{role.name}</td>
                  <td>
                    <button
                      onClick={() => toggleStatus(role)}
                      className={`status-badge ${role.is_active ? 'status-active' : 'status-inactive'}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      disabled={loading}
                    >
                      {role.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>{new Date(role.created_at).toLocaleDateString()}</td>
                  <td>{new Date(role.updated_at).toLocaleDateString()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openModal(role)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 8px', fontSize: '12px' }}
                      >
                        <Edit size={14} />
                      </button>
                      {canDelete(user) && (
                        <button
                          onClick={() => deleteRole(role.id)}
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          disabled={loading}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading roles...
          </div>
        )}

        {!loading && roles.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No roles found.
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Role Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter role name"
                  {...register('name', {
                    required: 'Role name is required',
                    minLength: {
                      value: 2,
                      message: 'Role name must be at least 2 characters'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Role Slug</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter role slug (e.g. admin, manager)"
                  {...register('slug', {
                    required: 'Role slug is required',
                    minLength: {
                      value: 2,
                      message: 'Role slug must be at least 2 characters'
                    },
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug can only contain lowercase letters, numbers, and hyphens'
                    }
                  })}
                />
                {errors.slug && (
                  <div className="error-message">{errors.slug.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
                {errors.is_active && (
                  <div className="error-message">{errors.is_active.message}</div>
                )}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleMaster;
